import {
  parseReserveResponse,
  type ReserveOutcome,
} from "@baluu/ui/lib/checkout/basket-reservations";
import type {
  BasketItem,
  BasketValidationResult,
} from "~/widgets/stores/basket-store";
import { apiOrigin, buildValidatePayload } from "./basket-validate";

/**
 * The basket overlay's calls to the reservation routes — the same requests
 * the widget's checkout makes (apps/widget/src/hooks/useBasketSync.ts). When
 * they are made, and why, is shared: `@baluu/ui/lib/checkout/basket-reservations`.
 */

/**
 * `POST /widget/basket/reserve`: hold the basket's seats for `buyerEmail`.
 * The items go as `/widget/basket/validate` takes them — the route validates
 * them first with the same service. A 409 resolves (some items could not be
 * held, `ok: false`); a network failure or another refusal rejects.
 */
export async function reserveBasket({
  apiUrl,
  schoolId,
  items,
  buyerEmail,
  userId,
}: {
  apiUrl: string;
  schoolId: string;
  items: BasketItem[];
  buyerEmail: string;
  /** The signed-in customer, recorded on the hold as the widget does. */
  userId?: string;
}): Promise<ReserveOutcome<BasketValidationResult>> {
  const response = await fetch(`${apiOrigin(apiUrl)}/widget/basket/reserve`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(userId ? { "x-user-id": userId } : {}),
    },
    body: JSON.stringify({
      items: buildValidatePayload(items),
      buyerEmail,
      schoolId,
    }),
  });
  const data = await response.json().catch(() => ({}));
  return parseReserveResponse<BasketValidationResult>(response.status, data);
}

/**
 * `POST /widget/basket/release`, best effort: by basket item when one is
 * removed, otherwise every ACTIVE hold this buyer has at the school. Sent
 * with `keepalive` so it survives the page going away; never rejects (the
 * expiry cron is the backstop).
 */
export async function releaseBasket({
  apiUrl,
  schoolId,
  buyerEmail,
  basketItemIds,
  reason,
}: {
  apiUrl: string;
  schoolId: string;
  buyerEmail?: string;
  basketItemIds?: string[];
  reason: string;
}): Promise<void> {
  try {
    await fetch(`${apiOrigin(apiUrl)}/widget/basket/release`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ basketItemIds, buyerEmail, schoolId, reason }),
      keepalive: true,
    });
  } catch {
    // Best effort, as above.
  }
}
